import { editor } from "./state.js";
import { getBoxById } from "./layout.js";

export function buildAdjacency() {
  const adjacency = new Map();
  for (const box of editor.boxes) {
    adjacency.set(box.id, []);
  }
  for (const edge of editor.edges) {
    if (!adjacency.has(edge.from)) {
      adjacency.set(edge.from, []);
    }
    adjacency.get(edge.from).push(edge.to);
  }
  return adjacency;
}

export function computeDistancesToStart(adjacency, startId) {
  const reverse = new Map();
  for (const [from, toList] of adjacency.entries()) {
    for (const to of toList) {
      if (!reverse.has(to)) {
        reverse.set(to, []);
      }
      reverse.get(to).push(from);
    }
    if (!reverse.has(from)) {
      reverse.set(from, []);
    }
  }
  const distances = new Map();
  const queue = [startId];
  distances.set(startId, 0);

  while (queue.length > 0) {
    const current = queue.shift();
    const dist = distances.get(current);
    const prevList = reverse.get(current) || [];
    for (const prev of prevList) {
      if (!distances.has(prev)) {
        distances.set(prev, dist + 1);
        queue.push(prev);
      }
    }
  }
  return distances;
}

export function pickClosestToStart(candidates, distancesToStart) {
  let bestId = candidates[0];
  let bestDist = distancesToStart.has(bestId) ? distancesToStart.get(bestId) : Infinity;
  for (let i = 1; i < candidates.length; i += 1) {
    const id = candidates[i];
    const dist = distancesToStart.has(id) ? distancesToStart.get(id) : Infinity;
    if (dist < bestDist) {
      bestDist = dist;
      bestId = id;
    }
  }
  return bestId;
}

export function pickStraightNext(previousId, currentId, candidates) {
  if (previousId == null) {
    return candidates[0];
  }
  const prevBox = getBoxById(previousId);
  const currentBox = getBoxById(currentId);
  if (!prevBox || !currentBox) {
    return candidates[0];
  }
  const inX = currentBox.x - prevBox.x;
  const inY = currentBox.y - prevBox.y;
  const inLen = Math.hypot(inX, inY);
  if (inLen === 0) {
    return candidates[0];
  }
  const inNx = inX / inLen;
  const inNy = inY / inLen;
  let bestId = candidates[0];
  let bestDot = -Infinity;
  for (const id of candidates) {
    const nextBox = getBoxById(id);
    if (!nextBox) {
      continue;
    }
    const outX = nextBox.x - currentBox.x;
    const outY = nextBox.y - currentBox.y;
    const outLen = Math.hypot(outX, outY);
    if (outLen === 0) {
      continue;
    }
    const outNx = outX / outLen;
    const outNy = outY / outLen;
    const dot = inNx * outNx + inNy * outNy;
    if (dot > bestDot) {
      bestDot = dot;
      bestId = id;
    }
  }
  return bestId;
}

export function buildStepPath(startId, steps, startBoxId) {
  const adjacency = buildAdjacency();
  const distancesToStart = startBoxId != null ? computeDistancesToStart(adjacency, startBoxId) : null;
  const path = [];
  let current = startId;
  let previous = null;
  for (let i = 0; i < steps; i += 1) {
    const nextList = adjacency.get(current) || [];
    if (nextList.length === 0) {
      break;
    }
    let nextId = nextList[0];
    if (nextList.length > 1) {
      if (distancesToStart && i === 0) {
        nextId = pickClosestToStart(nextList, distancesToStart);
      } else {
        nextId = pickStraightNext(previous, current, nextList);
      }
    }
    path.push(nextId);
    previous = current;
    current = nextId;
  }
  return path;
}

export function findPath(startId, targetId) {
  const adjacency = buildAdjacency();
  const queue = [startId];
  const prev = new Map();
  prev.set(startId, null);

  while (queue.length > 0) {
    const id = queue.shift();
    if (id === targetId) {
      break;
    }
    const neighbors = adjacency.get(id) || [];
    for (const next of neighbors) {
      if (!prev.has(next)) {
        prev.set(next, id);
        queue.push(next);
      }
    }
  }

  if (!prev.has(targetId)) {
    return null;
  }

  const path = [];
  let current = targetId;
  while (current !== startId) {
    path.push(current);
    current = prev.get(current);
    if (current == null) {
      return null;
    }
  }
  path.reverse();
  return path;
}
